const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

function getToken() {
  return localStorage.getItem('token') || localStorage.getItem('adminToken') || '';
}

// Timeout in ms per endpoint type
const AI_TIMEOUT_MS = 180_000;
const DEFAULT_TIMEOUT_MS = 15_000;

function isAiPath(path) {
  return path.startsWith('/api/generate')
    || path.startsWith('/api/edit')
    || path.startsWith('/api/meta/campaign')
    || path.startsWith('/api/assets/generate');
}

async function request(path, options = {}) {
  const headers = {
    ...(options.headers || {}),
  };

  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const controller = new AbortController();
  const timeoutMs = isAiPath(path) ? AI_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : await response.text();

  if (!response.ok) {
    // Auth routes (login/register): show a user-friendly credential error, no redirect
    const isAuthRoute = path.startsWith('/api/auth/');
    if (response.status === 401 && isAuthRoute) {
      throw new Error('Email or password is incorrect. Please try again.');
    }
    // For all other 401s: session expired → redirect to login
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/auth';
      throw new Error('Session expired. Please log in again.');
    }
    const message = typeof data === 'string'
      ? data
      : (data?.error?.message || data?.error || 'Request failed');
    throw new Error(message);
  }

  return data;
}

export async function uploadLogo(file) {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/assets/logo`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/auth';
      throw new Error('Session expired. Please log in again.');
    }
    throw new Error(data?.error?.message || 'Logo upload failed');
  }
  return data; // { url }
}

export async function register(payload) {
  return request('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function login(payload) {
  return request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function forgotPassword(payload) {
  return request('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function resetPassword(payload) {
  return request('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function generatePage({ description, provider, brand, lang }) {
  return request('/api/generate', {
    method: 'POST',
    body: JSON.stringify({ description, provider, brand, lang }),
  });
}

// Submit a landing page request (user-facing — no AI generation yet)
export async function requestPage({ description, brand }) {
  return request('/api/pages/request', {
    method: 'POST',
    body: JSON.stringify({ description, brand }),
  });
}

// Admin: list pending page requests
export async function adminListRequests() {
  return request('/api/admin/requests', { method: 'GET' });
}

// Admin: trigger AI generation for a pending request
export async function adminGeneratePage(requestId, provider = 'openai') {
  return request(`/api/admin/requests/${requestId}/generate`, {
    method: 'POST',
    body: JSON.stringify({ provider }),
  });
}

// Admin: publish a page
export async function adminPublishPage(pageId) {
  return request(`/api/admin/pages/${pageId}/publish`, { method: 'PATCH' });
}

// Admin: unpublish a page
export async function adminUnpublishPage(pageId) {
  return request(`/api/admin/pages/${pageId}/unpublish`, { method: 'PATCH' });
}

// Admin: wipe all content tables (keep users)
export async function adminResetData() {
  return request('/api/admin/reset-data', { method: 'POST' });
}

export async function editPage({ pageId, message, history, provider, overrideDesignLock = false, lang }) {
  // Filter out placeholder entries with null/empty content (e.g. the initial assistant greeting)
  const cleanHistory = (history || []).filter(
    (msg) => msg.content !== null && msg.content !== undefined && msg.content !== '',
  );
  return request('/api/edit', {
    method: 'POST',
    body: JSON.stringify({ pageId, message, history: cleanHistory, provider, overrideDesignLock, lang }),
  });
}

export async function listCrmJobs() {
  return request('/api/crm/jobs', {
    method: 'GET',
  });
}

export async function createCrmJob(payload) {
  return request('/api/crm/jobs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listCrmCandidates({ jobId, stage, page, pageSize, search } = {}) {
  const params = new URLSearchParams();
  if (jobId) {
    params.set('jobId', jobId);
  }
  if (stage) {
    params.set('stage', stage);
  }
  if (typeof page === 'number') {
    params.set('page', String(page));
  }
  if (typeof pageSize === 'number') {
    params.set('pageSize', String(pageSize));
  }
  if (search) {
    params.set('search', search);
  }

  const suffix = params.toString() ? `?${params.toString()}` : '';
  return request(`/api/crm/candidates${suffix}`, {
    method: 'GET',
  });
}

export async function updateCrmCandidateStage({ candidateId, stage }) {
  return request(`/api/crm/candidates/${candidateId}/stage`, {
    method: 'PATCH',
    body: JSON.stringify({ stage }),
  });
}

export async function getCrmCandidateDetail(candidateId) {
  return request(`/api/crm/candidates/${candidateId}`, {
    method: 'GET',
  });
}

export async function updateCrmCandidate({ candidateId, payload }) {
  return request(`/api/crm/candidates/${candidateId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function getPageDetails(pageId) {
  return request(`/api/pages/${pageId}`, {
    method: 'GET',
  });
}

export async function updatePageBrand(pageId, payload) {
  return request(`/api/pages/${pageId}/brand`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function uploadPageAsset({ pageId, file }) {
  const formData = new FormData();
  formData.append('pageId', pageId);
  formData.append('file', file);

  return request('/api/assets/upload', {
    method: 'POST',
    body: formData,
  });
}

export async function listPageAssets(pageId) {
  return request(`/api/assets/${pageId}`, {
    method: 'GET',
  });
}

export async function deletePageAsset(assetId) {
  return request(`/api/assets/${assetId}`, {
    method: 'DELETE',
  });
}

export async function generatePageAsset({ pageId, prompt }) {
  return request('/api/assets/generate', {
    method: 'POST',
    body: JSON.stringify({ pageId, prompt }),
  });
}

export function getLivePageUrl(pageId, cacheBust = '') {
  const suffix = cacheBust ? `?t=${encodeURIComponent(cacheBust)}` : '';
  return `${API_BASE}/p/${pageId}${suffix}`;
}

export async function translatePage({ pageId, lang }) {
  return request('/api/translate', {
    method: 'POST',
    body: JSON.stringify({ pageId, lang }),
  });
}

export async function listMetaCampaigns(userId) {
  const suffix = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  return request(`/api/meta/campaigns${suffix}`, { method: 'GET' });
}

export async function listMetaPagesByUser(userId) {
  return request(`/api/meta/pages?userId=${encodeURIComponent(userId)}`, { method: 'GET' });
}

export async function createMetaCampaign(payload) {
  return request('/api/meta/campaign', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function adminListUsers() {
  return request('/api/admin/users', { method: 'GET' });
}

export async function adminGetMetaToken() {
  return request('/api/admin/meta-token', { method: 'GET' });
}

export async function adminSaveMetaToken({ token, exchange }) {
  return request('/api/admin/meta-token', {
    method: 'POST',
    body: JSON.stringify({ token, exchange }),
  });
}

export async function updateMetaCampaignStatus({ id, status }) {
  return request(`/api/meta/campaign/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export { API_BASE };
